import React from 'react';
import Head from 'next/head';
import InfiniteScroll from 'react-infinite-scroller';
import Container from '@lendi-ui/container';
import { Article, Category } from '../utils/schema';
import { categoryUrl, loadArticles, loadCategories, has } from '../utils';
import { ArticlesContainer } from '../components/Article/index.style';
import { InfiniteScrollSpinner } from '../components/InfiniteScrollSpinner';
import TopArticleCard from '../components/TopArticleCard';
import ArticleCard from '../components/ArticleCard';
import { datadogRum } from '@datadog/browser-rum';

const PAGE_SIZE = 10;

const META_DEFAULTS = {
  title: 'Inspire',
  description: 'Get the latest home loan, property and design news tips and advice from our team of industry experts.',
  image: 'https://prodassets.lendi.com.au/lendi_logo_white_bg.jpg',
};

interface IndexPageProps {
  articles: Article[];
  categories: Category[];
  category?: Category;
}

interface IndexPageState {
  articles: any[];
  offset: number;
  hasMore: boolean;
  category?: Category;
}

const articleIds: string[] = [];

function filterExistingArticles(newArticles: Article[]) {
  const filteredArticles = newArticles.filter(({ id }) => {
    if (articleIds.indexOf(id) !== -1) {
      return false;
    }

    // Just in case, let's assume there might be some dups in the newArticles as well.
    articleIds.push(id);

    return true;
  });

  return filteredArticles;
}

class IndexPage extends React.Component<IndexPageProps, IndexPageState> {
  state: IndexPageState = {
    articles: this.props.articles || [],
    offset: 1,
    hasMore: this.props.articles.length === PAGE_SIZE,
  };

  componentDidMount() {
    articleIds.push(...this.state.articles.map(({ id }) => id));
  }

  componentDidUpdate = async (prevProps: IndexPageProps) => {
    if (this.props.category !== prevProps.category) {
      this.setState({
        articles: this.props.articles || [],
        offset: 1,
        hasMore: this.props.articles.length === PAGE_SIZE,
      });
    }
  };

  lazyLoading = async () => {
    const articles = await loadArticles({
      category: this.props.category,
      limit: PAGE_SIZE,
      skip: this.state.offset * PAGE_SIZE,
    });

    this.setState({
      articles: [...this.state.articles, ...filterExistingArticles(articles)],
      offset: this.state.offset + 1,
      hasMore: articles.length === PAGE_SIZE,
    });
  };

  render() {
    const { category } = this.props;
    const { articles, hasMore } = this.state;

    return (
      <>
        <Head>
          <title>
            {category && `${category.name} - `}
            {META_DEFAULTS.title} - Lendi
          </title>
          <meta name="description" content={category ? category.description : META_DEFAULTS.description} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={category ? category.name : META_DEFAULTS.title} />
          <meta property="og:description" content={category ? category.description : META_DEFAULTS.description} />
          <meta property="og:image" content={META_DEFAULTS.image} />
          <meta property="og:url" content={categoryUrl(category)} />
          <meta name="twitter:title" content={category ? category.name : META_DEFAULTS.title} />
          <meta name="twitter:description" content={category ? category.description : META_DEFAULTS.description} />
          <meta name="twitter:image" content={META_DEFAULTS.image} />
          <meta name="twitter:creator" content="@lendiau" />
          <link rel="canonical" href={categoryUrl(category)} />
        </Head>
        <Container>
          <InfiniteScroll
            pageStart={0}
            loadMore={this.lazyLoading}
            hasMore={hasMore}
            loader={<InfiniteScrollSpinner key={0} />}
          >
            <TopArticleCard article={articles[0]} />
            <ArticlesContainer>
              {articles.map(
                (article: Article, index) =>
                  index > 0 && <ArticleCard article={article} showSummary={true} key={article.id} />
              )}
            </ArticlesContainer>
          </InfiniteScroll>
        </Container>
      </>
    );
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    datadogRum.addError(error, errorInfo);
  }
}

export const getStaticProps = async (context) => {
  const categories = await loadCategories();
  const categorySlug = has(context, 'query.category') ? context.query.category : undefined;
  const category =
    categorySlug && categories ? categories.find((category) => category.slug === categorySlug) : undefined;

  const articles = await loadArticles({ category, limit: PAGE_SIZE, skip: 0 });

  return { props: JSON.parse(JSON.stringify({ articles, categories, category })) };
};

export default IndexPage;
